import { useFormikContext } from 'formik';
import { useState } from 'react';

import {
  Box,
  BoxProps,
  ClassNameMap,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  SlideProps,
  Typography,
  TypographyProps,
} from '@mui/material';
import { makeStyles  } from 'tss-react/mui';
import { ArrowBack, ArrowForward, Close, ExpandMore, Info } from '@mui/icons-material';
import { useIsMobile } from '@uplift/hooks';
import {
  Button, ButtonProps, ScrollableView, ScrollableViewProps, StickyStackItem
} from '@uplift/ui-v2';

export const StepContainer: React.FC<ScrollableViewProps> = props => <ScrollableView {...props} />;

export type StepContentProps = {
  className?: string;
};

export const StepContent: React.FC<StepContentProps> = props => {
  const { classes, cx } = useStepContentStyles();

  return <div className={cx(classes.root, props.className)}>{props.children}</div>;
};

const useStepContentStyles = makeStyles({ name: 'StepContent' })(theme => ({
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    width: '100%',
    maxWidth: `calc(640px + ${theme.spacing(4)})`,
    margin: '0 auto',
    padding: theme.spacing(3, 2),
    boxSizing: 'border-box',

    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(5, 2)
    },
  },
}));

export type StepTipPopoverProps = {
  children: React.ReactNode;
  title: string;
  classes?: Partial<ClassNameMap<'dialogContent'>>;
};

const TransitionComponent: React.VFC<SlideProps> = props => <Slide direction="up" {...props} />;

export const StepTipPopover: React.FC<StepTipPopoverProps> = props => {
  const [ open, setOpen ] = useState(false);
  const { classes } = useStepTipPopoverStyles(props, { props });

  const title = (
    <>
      <Info />
      <Typography variant="body2" color="textPrimary" className={classes.title}>
        {props.title}
      </Typography>
    </>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={TransitionComponent}
        scroll="body"
        classes={{
          paper: classes.dialogPaper,
          container: classes.dialogContainer,
        }}
      >
        <DialogTitle typography={null} className={classes.dialogTitle}>
          {title}
          <IconButton size="small" className={classes.button} onClick={() => setOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {props.children}
        </DialogContent>
      </Dialog>

      <StickyStackItem placement="bottom" order={1}>
        <Box className={classes.root} component="button" role="button" onClick={() => setOpen(true)}>
          {title}
          <IconButton size="small" className={classes.button}>
            <ExpandMore fontSize="small" />
          </IconButton>
        </Box>
      </StickyStackItem>
    </>
  );
};

const useStepTipPopoverStyles = makeStyles<StepTipPopoverProps>({ name: 'StepTipPopover' })(theme => ({
  root: {
    boxSizing: 'border-box',
    border: 0,
    cursor: 'pointer',
    margin: '0 auto',
    left: theme.spacing(1),
    width: `calc(100% - ${theme.spacing(2)})`,
    padding: theme.spacing(1.5, 2),
    background: theme.palette.info.background,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    boxShadow: theme.shadows[4],
    maxWidth: '600px',
    '& > svg': {
      color: theme.palette.info.main,
    },
    [theme.breakpoints.up('md')]: {
      left: 'unset',
    }
  },
  title: {
    fontWeight: 'bold',
    marginLeft: theme.spacing(1),
  },
  button: {
    marginLeft: 'auto'
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    '& > svg': {
      color: theme.palette.info.main,
    },
  },
  dialogPaper: {
    maxWidth: '600px !important',
    width: '100%',
    background: theme.palette.info.background,
    margin: theme.spacing(10, 0, 0)
  },
  dialogContent: {
    padding: theme.spacing(2),
  },
  dialogContainer: {
    padding: theme.spacing(0, 1),
  }
}));


export type StepNavigatorProps = {
  children?: React.ReactNode;
  onBack?: () => void;
  desktopOnly?: boolean;
};

export const StepNavigator: React.FC<StepNavigatorProps> = props => {
  const isMobile = useIsMobile();
  const { classes } = useStepNavigatorStyles();

  if (props.desktopOnly && isMobile) {
    return null;
  }

  return (
    <StickyStackItem placement="bottom" order={0}>
      <div className={classes.root}>
        {props.onBack && !isMobile && (
          <Button
            type="button"
            startIcon={<ArrowBack />}
            onClick={props.onBack}
            data-testid="step-container-back-btn"
          >
            Back
          </Button>
        )}
        <div className={classes.actions}>
          {props.children}
        </div>
      </div>
    </StickyStackItem>
  );
};

const useStepNavigatorStyles = makeStyles({ name: 'StepNavigator' })(theme => ({
  root: {
    background: theme.palette.background.paper,
    padding: theme.spacing(2),
    display: 'flex',
    borderTop: `1px solid ${theme.palette.divider}`,
    zIndex: theme.zIndex.appBar,
    boxSizing: 'border-box',
  },
  actions: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row-reverse',
    }
  }
}));

export const STEP_FORM_ID = 'step-form';

export const StepNextButton: React.VFC<ButtonProps> = props => {
  const formikContext = useFormikContext();

  return (
    <Button
      color="secondary"
      variant="contained"
      endIcon={<ArrowForward />}
      children="Next"
      form={STEP_FORM_ID}
      loading={formikContext?.isSubmitting ?? props.loading}
      onClick={formikContext?.handleSubmit}
      data-testid="step-container-next-btn"
      {...props}
    />
  );
};

export const StepHeading: React.FC<TypographyProps> = props => {
  return <Typography variant="h3" {...props}>{props.children}</Typography>;
};

export const StepDescription: React.FC<TypographyProps> = ({ className, ...props }) => {
  const { classes, cx } = useStepDescriptionStyles();

  return <Typography variant="body1" className={cx(classes.root, className)} {...props}>{props.children}</Typography>;
};

const useStepDescriptionStyles = makeStyles({ name: 'StepDescription' })(theme => ({
  root: {
    marginTop: theme.spacing(1),
  },
}));

export type StepFormProps = {
  children: React.ReactNode;
  onSubmit?: () => void;
} & Omit<BoxProps, 'onSubmit'>;

export const StepForm: React.FC<StepFormProps> = props => {
  return (
    <Box component="form" id={STEP_FORM_ID} mt={3} {...props}>
      {props.children}
    </Box>
  );
};

export type StepContentBoxProps = {
  title: string;
  description: string | React.ReactNode;
};

export const StepContentBox: React.FC<StepContentBoxProps> = ({ title, description }) => {
  const { classes } = useStepContentBoxStyles();

  return (
    <Box className={classes.contentContainer}>
      <Typography variant="body1" className={classes.contentTitle}>
        {title}
      </Typography>
      <Typography variant="body1" className={classes.contentDescription}>
        {description}
      </Typography>
    </Box>
  );
};

const useStepContentBoxStyles = makeStyles()((theme) => ({
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  contentTitle: {
    fontWeight: 700,
    letterSpacing: '0.5px',
  },
  contentDescription: {
    fontWeight: 400,
    letterSpacing: '0.5px',
  },
}));
